// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// Provider portal HOME (owner 2026-08-09: "/provider was a duplicate of My prescriptions").
// The lists moved to /provider/prescriptions and /provider/patients; this is the at-a-glance view.
// Counts come from GET /provider/summary, evaluated over the caller's whole prescriber scope — the
// same scope the tabs page through — so home cannot disagree with the pages it links to.

import type * as api from '../../api/client'

// D-PROV-2: the tiles are SERVER-computed now (GET /provider/summary).
//
// They used to be derived here by filtering the full `items` array with a DONE and an ATTENTION
// regex. That was only ever correct because the dashboard fetched every row; once the list is one
// page of 25, the same expressions answer "how many of these 25?" while the tile still reads as a
// practice total — a confidently wrong number, which is worse than the truncation it replaced.
// Those two patterns now live in GetProviderSummary as the equivalent SQL LIKE lists. They are kept
// here as the record of what that SQL mirrors, so a change to one is visibly a change to both:
//     DONE      = /cancel|void|expired|complete|discontinu|archiv/i
//     ATTENTION = /hold|pending|await|prior auth|\bpa\b/i

export default function ProviderHome({ summary, recent, loading, unread, navigate, portal = 'provider', sections }: {
  summary: api.ProviderSummary | null
  /** Just the newest page, for the "Recent prescriptions" strip — never for counting. */
  recent: api.ProviderRxItem[]
  loading: boolean
  unread: number
  navigate: (to: string) => void
  /** D-SOC-9: a facility reads the same numbers about its RESIDENTS, not its own prescribing. */
  portal?: 'provider' | 'facility'
  /** Card keys the pharmacy enabled for this surface (fac_home). null = never configured =
   *  show the defaults. Mario, 2026-09-25: "these views must be consistant home page with
   *  customizations from saas admin". */
  sections?: Set<string> | null
}) {
  const isFac = portal === 'facility'
  const base = isFac ? '/facility' : '/provider'
  const rxPath = `${base}/prescriptions`
  const ptPath = isFac ? '/facility/residents' : '/provider/patients'
  const show = (key: string) => !sections || sections.has(key)
  // `loading` covers the first paint; a null summary after that means the summary call failed, and
  // an em dash is the honest render — never a 0, which reads as "you have none".
  const num = (n: number | undefined) => loading ? '…' : (n === undefined ? '—' : n.toLocaleString())
  const activeRx = summary?.active_rx
  const activePatients = summary?.active_patients
  const needsAttention = summary?.needs_attention
  const recentRows = recent.slice(0, 6)

  const tile = (label: string, value: React.ReactNode, sub: string, onClick?: () => void) => (
    <div key={label} onClick={onClick} style={{
      border: '1px solid var(--color-border)', borderRadius: 12, padding: '14px 16px',
      background: 'var(--color-surface)', cursor: onClick ? 'pointer' : 'default', flex: '1 1 170px', minWidth: 150,
    }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: .4, color: 'var(--color-text-mute)', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 750, lineHeight: 1.15, margin: '3px 0' }}>{value}</div>
      <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)' }}>{sub}</div>
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {show('active_rx') && tile('Active prescriptions', num(activeRx),
          isFac ? `of ${num(summary?.total_rx)} for your residents` : `of ${num(summary?.total_rx)} you have written`,
          () => navigate(rxPath))}
        {show('active_patients') && tile(isFac ? 'Active residents' : 'Active patients', num(activePatients),
          summary?.total_patients ? `${num(summary.total_patients)} total on file` : 'with a live prescription',
          () => navigate(ptPath))}
        {show('needs_attention') && tile('Needs attention', num(needsAttention),
          'on hold or awaiting authorization', () => navigate(rxPath))}
        {show('unread') && tile('Unread messages', unread, 'in your inbox', () => navigate('/notifications'))}
      </div>

      {show('recent_rx') && <div style={{ border: '1px solid var(--color-border)', borderRadius: 12, background: 'var(--color-surface)' }}>
        <div style={{ padding: '11px 15px', borderBottom: '1px solid var(--color-border)', fontSize: 13, fontWeight: 700 }}>
          Recent prescriptions
        </div>
        {loading ? (
          <div style={{ padding: 15, fontSize: 12.5, color: 'var(--color-text-mute)' }}>Loading…</div>
        ) : recentRows.length === 0 ? (
          <div style={{ padding: 15, fontSize: 12.5, color: 'var(--color-text-mute)' }}>
            {isFac ? 'No prescriptions yet for residents of your facilities.' : 'No prescriptions yet. Once the pharmacy links your prescriber record, everything you have written appears here.'}
          </div>
        ) : recentRows.map(r => (
          <div key={r.id} onClick={() => navigate(rxPath)} style={{
            padding: '9px 15px', borderTop: '1px solid var(--color-border)', display: 'flex',
            gap: 12, alignItems: 'baseline', cursor: 'pointer', fontSize: 12.5,
          }}>
            <span style={{ fontWeight: 600, minWidth: 92 }}>{r.rx_number ?? '—'}</span>
            <span style={{ flex: 1 }}>{r.drug_name ?? '—'}</span>
            <span style={{ color: 'var(--color-text-mute)' }}>{r.patient_name ?? ''}</span>
            <span style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>{r.state ?? ''}</span>
          </div>
        ))}
      </div>}
    </div>
  )
}
